import { useState, useEffect, useMemo } from 'react';
import {
  Skull, AlertTriangle, TrendingDown, Loader2, Play, CheckCircle, RefreshCw,
  BarChart3, Activity, Info, ChevronRight, Search, Settings,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  listStressScenarios, runStressScenario,
  listStrategies, getExchangeConnections, getExchangeMarkets,
} from '../services/api';
import BacktestVisualizations from '../components/BacktestVisualizations';
import { InfoTip } from '../components/ui';

const SEVERITY_STYLE = {
  low:      { color: 'emerald', label: 'LOW' },
  medium:   { color: 'amber',   label: 'MEDIUM' },
  high:     { color: 'orange',  label: 'HIGH' },
  critical: { color: 'red',     label: 'CRITICAL' },
};

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400' },
  orange:  { bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  text: 'text-orange-400' },
  red:     { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-400' },
};

const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BTC/USDC', 'ETH/USDC', 'SOL/USDC'];

function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    return (POPULAR_QUOTES.indexOf(quote) === -1 ? 100 : POPULAR_QUOTES.indexOf(quote)) * 1000
      + (POPULAR_BASES.indexOf(base) === -1 ? 999 : POPULAR_BASES.indexOf(base));
  };
  return [...pairs].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export default function StressTestPage() {
  const { user } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // Strategy from user's library (replaces the old "type JSON" textarea).
  // If null, the legacy fixed-logic RSI+EMA strategy runs — same as before.
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [strategyId, setStrategyId] = useState('');

  // Connected exchanges (replaces hardcoded ['binance', 'coinbase', ...])
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [exchange, setExchange] = useState('binance');

  // Per-exchange searchable pair list (replaces hardcoded 3 symbols)
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');
  const [symbol, setSymbol] = useState('BTC/USDT');

  const [initialCapital, setInitialCapital] = useState(10000);
  const [feeBps, setFeeBps] = useState(40);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Custom date range — when selectedId='custom', user picks their own window
  // against real OHLCV. Default to "last 90 days" so user has a starting point.
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [customStartDate, setCustomStartDate] = useState(ninetyDaysAgo);
  const [customEndDate, setCustomEndDate] = useState(today);

  // Load scenarios + saved strategies + connected exchanges in parallel
  useEffect(() => {
    Promise.all([
      listStressScenarios().then(r => r?.scenarios || r?.data?.scenarios || []).catch(() => []),
      listStrategies().then(r => r?.data?.strategies || r?.strategies || []).catch(() => []),
      getExchangeConnections().then(r => (r?.exchanges || r?.data?.exchanges || []).filter(e => e.connected)).catch(() => []),
    ]).then(([sc, ss, ex]) => {
      setScenarios(sc);
      setSavedStrategies(ss);
      setConnectedExchanges(ex);
      if (ex.length && !ex.find(e => e.exchangeId === exchange)) setExchange(ex[0].exchangeId);
    }).catch(err => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load markets when exchange changes — same pattern as Strategy Builder
  useEffect(() => {
    if (!exchange) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(exchange)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || res?.data?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(symbol)) setSymbol(sorted[0]);
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange]);

  const filteredPairs = useMemo(() => {
    return pairSearch
      ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 200)
      : pairs.slice(0, 200);
  }, [pairs, pairSearch]);

  // The "custom" scenario is synthesized client-side. All others come from
  // the backend's curated catalog.
  const customScenario = {
    id: 'custom',
    label: 'Custom Date Range',
    severity: 'medium',
    summary: 'Pick any historical window (real exchange OHLCV, just user-chosen dates). Min 7 days, max 2 years.',
    startDate: customStartDate,
    endDate: customEndDate,
    suggestedTimeframe: '1h',
    defaultSymbol: 'BTC/USDT',
    isCustom: true,
  };
  const selected = selectedId === 'custom'
    ? customScenario
    : scenarios.find(s => s.id === selectedId);

  // Auto-set sensible defaults when a scenario is picked
  useEffect(() => {
    if (selected?.defaultSymbol && pairs.includes(selected.defaultSymbol)) {
      setSymbol(selected.defaultSymbol);
    }
  }, [selectedId, selected, pairs]);

  async function handleRun() {
    if (!selectedId) return;
    setError(null);
    setResult(null);
    setRunning(true);

    const payload = {
      scenarioId: selectedId,
      exchange,
      symbol,
      timeframe: selected?.suggestedTimeframe || '1h',
      initialCapital: parseFloat(initialCapital) || 10000,
      feeBps: parseFloat(feeBps) || 40,
      strategyId: strategyId || null,
    };
    // For custom scenario, backend needs the user-supplied dates
    if (selectedId === 'custom') {
      payload.customStartDate = customStartDate;
      payload.customEndDate = customEndDate;
    }

    try {
      const res = await runStressScenario(payload);
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <Skull className="w-7 h-7 text-red-400" />
          <h1 className="text-2xl font-bold text-white">Stress Test Library</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">{scenarios.length || ''} scenarios</span>
        </div>
        <p className="text-sm text-gray-400">
          Run your strategy through the worst weeks in crypto history. <span className="text-emerald-400">Real OHLCV data via CCXT — no simulation.</span> Tells you the truth about how your strategy would have survived.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Scenario list */}
        <div className="lg:col-span-1 space-y-2">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Pick a scenario</p>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>
          ) : scenarios.length === 0 ? (
            <p className="text-xs text-gray-500">No scenarios available.</p>
          ) : (
            <>
              {scenarios.map(s => {
                const sev = SEVERITY_STYLE[s.severity] || SEVERITY_STYLE.medium;
                const c = COLOR_CLASSES[sev.color];
                const active = selectedId === s.id;
                return (
                  <button key={s.id} onClick={() => { setSelectedId(s.id); setResult(null); }}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${active ? 'bg-red-500/10 border-red-400/40' : 'bg-dark-800/50 border-dark-700/50 hover:border-dark-600'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-bold text-white">{s.label}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${c.bg} ${c.border} ${c.text}`}>{sev.label}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1 font-mono">{s.startDate} → {s.endDate}</p>
                    {s.expectedDrawdownPct && (
                      <p className="text-[10px] text-red-400 mt-0.5">Expected drawdown: {s.expectedDrawdownPct}%</p>
                    )}
                  </button>
                );
              })}

              {/* Custom Range — user-defined window against real OHLCV */}
              <button onClick={() => { setSelectedId('custom'); setResult(null); }}
                className={`w-full text-left p-3 rounded-xl border-2 border-dashed transition-all mt-2 ${
                  selectedId === 'custom'
                    ? 'bg-violet-500/10 border-violet-400/50'
                    : 'bg-dark-800/30 border-dark-600/50 hover:border-violet-500/30'
                }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-violet-300">+ Custom Date Range</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-400">CUSTOM</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Pick any historical window — real OHLCV, just your dates.</p>
              </button>
            </>
          )}
        </div>

        {/* Right: Run config + Result */}
        <div className="lg:col-span-2 space-y-4">
          {!selected ? (
            <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
              <Skull className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">Select a stress scenario</p>
              <p className="text-sm text-gray-500">Pick from the list to run your strategy through that historical window.</p>
            </div>
          ) : (
            <>
              <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
                <h3 className="text-base font-bold text-white mb-1">{selected.label}</h3>
                <p className="text-xs text-gray-400 mb-4">{selected.summary}</p>

                {/* Custom date range pickers — only when 'custom' scenario selected */}
                {selectedId === 'custom' && (
                  <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 mb-4">
                    <p className="text-[10px] uppercase tracking-wider text-violet-300 mb-2 font-bold">Custom range</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-gray-500 mb-1 block">Start date</label>
                        <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)}
                          max={customEndDate}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 mb-1 block">End date</label>
                        <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)}
                          min={customStartDate} max={today}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono" />
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">
                      Min 7 days, max 2 years. Tested against real {exchange} OHLCV.
                    </p>
                  </div>
                )}

                {/* Strategy picker — pulls from user's Strategy Builder library.
                    If empty, uses the legacy fixed RSI+EMA strategy (with a
                    visible callout so users know what they're testing). */}
                <div className="mb-4">
                  <label className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                    <Settings className="w-3 h-3" /> Strategy to test
                    <InfoTip side="right">Pick a strategy you built in Strategy Builder. Without one, you test a generic baseline — useful for calibrating the engine but not for validating YOUR rules.</InfoTip>
                  </label>
                  {savedStrategies.length === 0 ? (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
                      No saved strategies yet. <a href="/strategy-builder" className="underline">Build one in Strategy Builder</a> first to test your actual conditions against this scenario.
                      <br />
                      <span className="text-amber-400/70 mt-1 inline-block">Running anyway will use a generic RSI &lt; 35 + EMA9 &gt; EMA20 strategy — useful as a baseline but not your actual logic.</span>
                    </div>
                  ) : (
                    <>
                      <select value={strategyId} onChange={e => setStrategyId(e.target.value)}
                        className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono">
                        <option value="">— Generic RSI+EMA baseline (no saved strategy) —</option>
                        {savedStrategies.map(s => (
                          <option key={s.strategyId} value={s.strategyId}>
                            {s.name}{s.category ? ` (${s.category})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {strategyId
                          ? 'Runs your saved strategy\'s exact conditions + actions + risk controls through the historical period.'
                          : 'No strategy selected — will use a fixed-logic baseline so you have something to compare your real strategy against.'}
                      </p>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] text-gray-500 mb-1 block">Exchange</label>
                    {connectedExchanges.length === 0 ? (
                      <div className="w-full bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 text-amber-300 text-[10px]">
                        Connect a CEX in <a href="/settings?tab=connections" className="underline">Settings</a>
                      </div>
                    ) : (
                      <select value={exchange} onChange={e => setExchange(e.target.value)}
                        className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono">
                        {connectedExchanges.map(ex => (
                          <option key={ex.exchangeId} value={ex.exchangeId}>{ex.name || ex.exchangeId}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 mb-1 block">Timeframe</label>
                    <input value={selected.suggestedTimeframe || '1h'} disabled
                      className="w-full bg-dark-700/50 border border-dark-600 rounded-lg p-2 text-gray-400 text-xs font-mono" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                      Initial capital ($)
                      <InfoTip side="top">Use 10000 — the round number makes % drawdown easy to read. Result scales linearly so the absolute capital does not change strategy quality.</InfoTip>
                    </label>
                    <input type="number" value={initialCapital} onChange={e => setInitialCapital(e.target.value)}
                      className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                      Fee bps
                      <InfoTip side="top">100 bps = 1%. 40 bps = Coinbase / Binance US taker (typical). A strategy that needs 0 bps to be profitable is not actually profitable.</InfoTip>
                    </label>
                    <input type="number" value={feeBps} onChange={e => setFeeBps(e.target.value)} min={0} max={200} step={5}
                      className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono" />
                  </div>
                </div>

                {/* Symbol — searchable per-exchange pair list, same pattern as Strategy Builder */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] text-gray-500 flex items-center gap-1">
                      Symbol
                      {pairsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    </label>
                    <span className="text-[10px] text-gray-500">
                      {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} pairs on ${exchange}`}
                    </span>
                  </div>
                  <div className="relative mb-1">
                    <Search className="w-3 h-3 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                      value={pairSearch}
                      onChange={e => setPairSearch(e.target.value)}
                      placeholder="Search pair (e.g. BTC, ETH/USDC)"
                      className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-6 pr-2 py-1.5 text-white text-xs font-mono"
                    />
                  </div>
                  <div className="max-h-32 overflow-y-auto bg-dark-700/40 border border-dark-600 rounded-lg">
                    {filteredPairs.length === 0 ? (
                      <div className="p-2 text-[10px] text-gray-500">No pairs match "{pairSearch}"</div>
                    ) : (
                      filteredPairs.map(p => (
                        <button key={p} type="button" onClick={() => setSymbol(p)}
                          className={`block w-full text-left px-2 py-1 text-[11px] font-mono transition-colors ${
                            symbol === p ? 'bg-red-500/15 text-red-300' : 'text-gray-300 hover:bg-dark-600'
                          }`}>
                          {p}
                        </button>
                      ))
                    )}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Selected: <span className="font-mono text-white">{symbol}</span></p>
                </div>

                {error && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs mb-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                  </div>
                )}

                <button onClick={handleRun} disabled={running}
                  className="w-full bg-red-500 hover:bg-red-400 disabled:bg-dark-600 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
                  {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Fetching real OHLCV + running backtest…</> : <><Play className="w-4 h-4" /> Run stress test</>}
                </button>
              </div>

              {/* Result */}
              {result && (
                <>
                  <ResultCard result={result} initialCapital={initialCapital} />
                  <BacktestVisualizations result={result} initialCapital={parseFloat(initialCapital) || 10000} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bg-dark-800/30 rounded-2xl border border-dark-700/30 p-4">
        <p className="text-[10px] text-gray-500 flex items-start gap-1.5">
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          All scenarios use real historical OHLCV from CCXT. The strategy is evaluated on actual price action from the chosen window — not synthetic data. Past performance does not predict future results, but a strategy that fails one of these tests is statistically very likely to fail in similar future events.
        </p>
      </div>
    </div>
  );
}

function ResultCard({ result, initialCapital }) {
  const finalEquity = result.finalEquity ?? null;
  const totalReturnPct = result.totalReturnPct ?? null;
  const trades = result.trades || [];
  // Filter to CLOSED trades only — entries have no pnl. New engine uses
  // `pnlUsd`, old uses `pnl`. Win rate from total trade count would be wrong
  // because half the entries are buys (no PnL until paired with sell).
  const closed = trades.filter(t => (t.pnlUsd != null) || (t.pnl != null && t.pnl !== 0));
  const tradeCount = closed.length || result.totalTrades || trades.length;
  const wins = closed.filter(t => (t.pnlUsd ?? t.pnl ?? 0) > 0).length;
  // Prefer engine-reported winRate (new engine emits 0-1 decimal, old emits string)
  let winRate;
  if (typeof result.winRate === 'number') {
    winRate = result.winRate <= 1 ? result.winRate * 100 : result.winRate;
  } else if (closed.length > 0) {
    winRate = (wins / closed.length) * 100;
  } else {
    winRate = null;
  }
  const maxDrawdown = result.maxDrawdownPct ?? result.metrics?.maxDrawdown ?? null;
  const isLoss = totalReturnPct != null && totalReturnPct < 0;

  return (
    <div className={`rounded-2xl border p-5 ${isLoss ? 'bg-red-500/5 border-red-500/30' : 'bg-emerald-500/5 border-emerald-500/30'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          {isLoss ? <TrendingDown className="w-5 h-5 text-red-400" /> : <CheckCircle className="w-5 h-5 text-emerald-400" />}
          Result
        </h3>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isLoss ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
          {isLoss ? 'Losing test' : 'Survived'}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Final equity" value={`$${formatNum(finalEquity, 2)}`} accent={isLoss ? 'red' : 'emerald'} />
        <Stat label="Total return" value={`${totalReturnPct >= 0 ? '+' : ''}${formatNum(totalReturnPct, 2)}%`} accent={isLoss ? 'red' : 'emerald'} />
        <Stat label="Trades" value={tradeCount} />
        <Stat label="Win rate" value={winRate != null ? `${formatNum(winRate, 1)}%` : '—'} />
      </div>

      {maxDrawdown !== null && (
        <div className="text-xs text-gray-400 mb-2">
          Max drawdown: <span className="text-red-400 font-mono font-bold">{formatNum(maxDrawdown, 2)}%</span>
        </div>
      )}

      <p className="text-[10px] text-gray-500 mt-3">
        Backtest ID: <span className="font-mono text-gray-400">{result.backtestId?.slice(0, 8) || '—'}</span> · Real OHLCV data · Strategy ran on every candle in the window
      </p>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400' };
  return (
    <div className="bg-dark-700/40 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
    </div>
  );
}
